if (!customElements.get('sticky-add-to-cart-bar')) {
    customElements.define(
        'sticky-add-to-cart-bar',
        class StickyAddToCartBar extends HTMLElement {
            constructor() {
                super();
                this.intersectionObserver = null;
                this.priceObserver = null;
                this.cartUpdateUnsubscriber = null;
                this.cartErrorUnsubscriber = null;
                this.variantChangeUnsubscriber = null;
                this.variantSelectsVisible = true;
                this.productFormVisible = true;
            }

            connectedCallback() {
                this.productInfo = this.closest('product-info');
                if (!this.productInfo) return;

                this.sectionId = this.dataset.sectionId || this.productInfo.dataset.section;
                this.formId = this.dataset.productFormId || `product-form-${this.sectionId}`;

                this.form = this.productInfo.querySelector(`#${this.formId}`) || this.productInfo.querySelector('product-form form');
                const productForm = this.productInfo.querySelector('product-form');
                this.form = this.form || this.productInfo.querySelector(`#${this.formId}`) || productForm?.querySelector?.('form');

                this.variantSelectsTarget = this.productInfo.querySelector(`#variant-selects-${this.sectionId}`);
                this.productFormTarget = productForm || this.form;

                if (!this.productFormTarget) return;

                this.priceTarget = this.querySelector('.sticky-add-to-cart-bar__price');
                this.mainPriceEl = this.productInfo.querySelector(`#price-${this.sectionId}`);
                this.submitBtn = this.querySelector('.sticky-add-to-cart-bar__submit');
                this.submitText = this.querySelector('.sticky-add-to-cart-bar__submit-text');
                this.mainSubmitId = this.submitBtn?.dataset.mainSubmitId || `ProductSubmitButton-${this.sectionId}`;

                this.setupIntersectionObserver();
                this.syncFromMainForm();
                this.syncThumbnailFromGallery();
                this.setupPriceSync();
                this.setupVariantSync();
                this.setupSubmitButton();
                this.setupCartListeners();
                this.setupVariantChangeListener();
            }

            disconnectedCallback() {
                this.intersectionObserver?.disconnect();
                this.priceObserver?.disconnect();
                this.cartUpdateUnsubscriber?.();
                this.cartErrorUnsubscriber?.();
                this.variantChangeUnsubscriber?.();
                clearTimeout(this._stickyBarChangeTimeout);
            }

            setupIntersectionObserver() {
                const ioOptions = { root: null, rootMargin: '0px 0px -60px 0px', threshold: 0 };
                const updateVisibility = () => {
                    const show = !this.variantSelectsVisible && !this.productFormVisible;
                    this.classList.toggle('sticky-add-to-cart-bar--visible', show);
                    this.setAttribute('aria-hidden', show ? 'false' : 'true');
                };
                const handler = (entries) => {
                    entries.forEach((entry) => {
                        const visible = entry.isIntersecting;
                        if (entry.target === this.variantSelectsTarget) {
                            this.variantSelectsVisible = visible;
                        } else if (entry.target === this.productFormTarget) {
                            this.productFormVisible = visible;
                        }
                        updateVisibility();
                    });
                };
                this.intersectionObserver = new IntersectionObserver(handler, ioOptions);
                this.intersectionObserver.observe(this.productFormTarget);
                if (this.variantSelectsTarget) {
                    this.intersectionObserver.observe(this.variantSelectsTarget);
                } else {
                    this.variantSelectsVisible = false;
                }
            }

            syncFromMainForm() {
                if (this.mainPriceEl && this.priceTarget) {
                    this.priceTarget.innerHTML = this.mainPriceEl.innerHTML;
                }
                const mainSubmit = this.productInfo?.querySelector(`#${this.mainSubmitId}`);
                if (mainSubmit && this.submitBtn) {
                    const span = mainSubmit.querySelector('span:not(.loading__spinner):not(.sold-out-message)');
                    this.submitBtn.disabled = mainSubmit.disabled || mainSubmit.getAttribute('aria-disabled') === 'true';
                    this.submitBtn.setAttribute('aria-disabled', mainSubmit.getAttribute('aria-disabled') || (mainSubmit.disabled ? 'true' : 'false'));
                    if (this.submitText && span) this.submitText.textContent = span.textContent;
                }
            }

            getMainVariantSelects() {
                return this.productInfo?.querySelector(`#variant-selects-${this.sectionId}`);
            }

            syncStickyBarSelectsFromMain() {
                const variantSelects = this.getMainVariantSelects();
                if (!variantSelects) return;
                const containers = variantSelects.querySelectorAll('.product-form__input');
                containers.forEach((container, index) => {
                    const stickySelect = this.querySelector(`.sticky-add-to-cart-bar__select[data-option-index="${index}"]`);
                    if (!stickySelect) return;
                    const mainSelect = container.querySelector('select');
                    const mainInput = mainSelect || container.querySelector('input:checked');
                    const value = mainInput?.value;
                    if (value && stickySelect.value !== value) {
                        stickySelect.value = value;
                    }
                });
                this.syncThumbnailFromGallery();
                this.syncFromMainForm();
            }

            syncThumbnailFromGallery() {
                const galleryImg = this.productInfo?.querySelector('.product__media-item.is-active img, .product__media-item--single img');
                const thumbnail = this.querySelector('.sticky-add-to-cart-bar__thumbnail');
                if (galleryImg?.src && thumbnail) {
                    thumbnail.src = galleryImg.currentSrc || galleryImg.src;
                }
            }

            triggerMainVariantChange(stickySelect) {
                this.pendingScrollRestore = window.scrollY;
                document.body.setAttribute('data-sticky-bar-variant-change', 'true');
                clearTimeout(this._stickyBarChangeTimeout);
                this._stickyBarChangeTimeout = setTimeout(() => {
                    document.body.removeAttribute('data-sticky-bar-variant-change');
                }, 3000);
                const variantSelects = this.getMainVariantSelects();
                if (!variantSelects) return;
                const optionIndex = parseInt(stickySelect.dataset.optionIndex, 10);
                const containers = variantSelects.querySelectorAll('.product-form__input');
                const container = containers?.[optionIndex];
                if (!container) return;
                const mainSelect = container.querySelector('select');
                const mainInput = mainSelect || container.querySelector(`input[value="${CSS.escape(stickySelect.value)}"]`);
                if (mainInput) {
                    if (mainSelect) {
                        mainSelect.value = stickySelect.value;
                    } else {
                        mainInput.checked = true;
                    }
                    mainInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }

            setupPriceSync() {
                if (!this.mainPriceEl) return;
                this.priceObserver = new MutationObserver(() => this.syncFromMainForm());
                this.priceObserver.observe(this.mainPriceEl, { childList: true, subtree: true });
            }

            setupVariantSync() {
                this.querySelectorAll('.sticky-add-to-cart-bar__select').forEach((select) => {
                    select.addEventListener('change', () => {
                        this.triggerMainVariantChange(select);
                        this.syncFromMainForm();
                    });
                });
                this.productInfo?.addEventListener('change', (e) => {
                    if (e.target.closest(`#variant-selects-${this.sectionId}`)) {
                        this.syncStickyBarSelectsFromMain();
                    }
                });
            }

            setupVariantChangeListener() {
                if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
                this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
                    if (data?.sectionId !== this.sectionId) return;
                    this.syncStickyBarSelectsFromMain();
                    if (this.pendingScrollRestore !== undefined) {
                        requestAnimationFrame(() => window.scrollTo(0, this.pendingScrollRestore));
                        this.pendingScrollRestore = undefined;
                        clearTimeout(this._stickyBarChangeTimeout);
                        document.body.removeAttribute('data-sticky-bar-variant-change');
                    }
                    this.reobserveVariantElements();
                });
            }

            reobserveVariantElements() {
                const newVariantSelects = this.getMainVariantSelects();
                const newProductForm = this.productInfo?.querySelector('product-form') || this.form;
                if (!newProductForm) return;
                const targetsChanged =
                    newVariantSelects !== this.variantSelectsTarget || newProductForm !== this.productFormTarget;
                if (!targetsChanged) return;
                this.intersectionObserver?.disconnect();
                this.variantSelectsTarget = newVariantSelects;
                this.productFormTarget = newProductForm;
                if (!this.variantSelectsTarget) this.variantSelectsVisible = false;
                this.intersectionObserver?.observe(this.productFormTarget);
                if (this.variantSelectsTarget) {
                    this.intersectionObserver?.observe(this.variantSelectsTarget);
                }
            }

            setupSubmitButton() {
                this.submitBtn?.addEventListener('click', (e) => {
                    if (this.submitBtn.disabled || this.submitBtn.getAttribute('aria-disabled') === 'true') return;
                    e.preventDefault();
                    const mainSubmit = document.getElementById(this.mainSubmitId);
                    if (!mainSubmit || mainSubmit.disabled || mainSubmit.getAttribute('aria-disabled') === 'true') return;
                    this.submitBtn.classList.add('loading');
                    this.submitBtn.setAttribute('aria-disabled', 'true');
                    this.submitBtn.querySelector('.loading__spinner')?.classList.remove('hidden');
                    mainSubmit.click();
                });
            }

            setupCartListeners() {
                if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
                this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.removeLoading());
                this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => this.removeLoading());
            }

            removeLoading() {
                if (this.submitBtn) {
                    this.submitBtn.classList.remove('loading');
                    this.submitBtn.querySelector('.loading__spinner')?.classList.add('hidden');
                }
                this.syncFromMainForm();
            }
        }
    );
}
